import { stringArrayToSelectOptions } from '@/utils/misc.ts'
import { Approval } from '@/common/types/approval.def.ts'
import { HcFile } from '@/common/types/personal-info.def.ts'
import { SelectGroupOption, SelectOption } from '@/common/types/components.def.ts'

export type TimeOffRequest = {
  id: number
  employeeId: string
  year: number
  country: string
  employmentEntity: string
  timeZoneId: string
  isHourlyRequest: boolean
  localStartTime: string
  localEndTime: string
  localApplicationTime: string
  utcApplicationTime: string
  status: string
  currentYearAmount: number
  nextYearAmount: number
  supportingDocuments: HcFile[]
  note: string | null
  timeOffType: TimeOffType
  timeOffTypeId: number
  approval?: Approval
  managerActionTimestamp?: string | null
  managerAction?: string | null
  managerEid?: string | null
  hcActionTimestamp?: string | null
  hcAction?: string | null
  hcEid?: string | null
}

export type TimeOffType = {
  id: number
  timeOffPolicyId: number
  country: string
  name: string
  accrualType: string
  accrualAmount: number
  canUseFutureBalance: boolean
  supportHourly: boolean
  supportPartialDay: boolean
  payPercent: number
  supportingDocRequired: boolean
  hcApprovalRequired: boolean
  carryForwardType: string
  carryForwardLimit: number
  carryForwardExpiryDate?: string | null
  allowTakenInNonWorkingDay: boolean
  remarks?: string | null
  isDisabled: boolean
}

export type TimeOffAllocationAdjustment = {
  allocationName: string
  allocationId: number
  availableBefore: number
  availableAfter: number
  availableDelta: number
  localExpiryTimeBefore?: string | null
  localExpiryTimeAfter?: string | null
}

export type TimeOffBalanceHistory = {
  id: number
  employeeId: string
  timeZoneId: string
  localTxTime: string
  utcTxTime: string
  eventType: string
  timeOffTypeId: number
  timeOffType: TimeOffType
  timeOffRequestId?: number | null
  timeOffRequest?: TimeOffRequest | null
  amount: number
  balanceBefore: number
  balanceAfter: number
  remarks?: string | null
  allocationAdjustment?: TimeOffAllocationAdjustment
}

export type TimeOffAllocation = {
  id: number
  employeeId: string
  name: string
  country: string
  timeZoneId: string
  timeOffTypeId: number
  allocationType: string
  localExpiryTime: string
  utcExpiryTime: string
  grantedAmount: number
  availableAmount: number
  consumedAmount: number
  status: string
  remarks?: string
  isMain: boolean
}

export type TimeOffBalance = {
  id: number
  employeeId: string
  country: string
  year: number
  current: number
  consumedAmount: number
  pendingDeduction: number
  timeOffType: TimeOffType
  timeOffTypeId: number
  timeOffAllocations: TimeOffAllocation[]
}

export type GetTimeOffBalanceResponse = {
  balances: TimeOffBalance[]
  // The employee's currently-attached (active) time off policy, or null if the
  // employee has no active enrollment.
  attachedPolicy?: TimeOffPolicy
}

/**
 * A day on the country calendar that deviates from the default working-week rule.
 * - `Holiday`: always a non-working day (the old "public holiday" concept).
 * - `Makeup Workday`: a weekend day designated as a normal working day
 *   (e.g. China's compensatory makeup weekends).
 */
export const EventDayType = {
  Holiday: 'Holiday',
  MakeupWorkday: 'Makeup Workday',
} as const
export type EventDayTypeValue = typeof EventDayType[keyof typeof EventDayType]

export const EventDayTypeOptions = stringArrayToSelectOptions(Object.values(EventDayType))

export type EventDay = {
  date: string
  name: string
  /**
   * Always present on responses: the backend substitutes `Holiday` when a stored
   * or imported row omits it, so the client never has to defend against a missing
   * value. It must still be sent explicitly on writes.
   */
  type: EventDayTypeValue
}

export type CalendarConfig = {
  id: number
  country: string
  timeZoneId: string
  year: number
  eventDays: EventDay[]
  createdAt: string
  updatedAt: string
  createdBy: string
  updatedBy: string
}

export type MonthStep = {
  months: number
  comparison: string
  steps: number
}

export type ProrationLogic = {
  timeOffTypeId: number
  timeOffTypeName: string
  prorationType: string
  description: string
  accrualAmount: number
  monthSteps: MonthStep[]
}

export type TimeOffPolicy = {
  id: number
  name: string
  country: string
  remarks?: string | null
  disabledAt?: string | null
  isDisabled: boolean
}

export type TimeOffPolicyConfig = TimeOffPolicy & {
  timeOffTypes: TimeOffType[]
  prorationLogicDetails: ProrationLogic[]
}

export type CountryTimeOffConfig = {
  country: string
  eventDays: EventDay[]
  policies: TimeOffPolicyConfig[]
}

export type TimeOffOnboardingPreviewItem = {
  timeOffTypeId: number
  timeOffTypeName: string
  allocationName: number
  proratedAmount: number
  accrualAmount: number
}

export type TimeOffOnboardingPreviewResult = {
  employeeId: string
  country: string
  refStartDate: string
  refEndDate: string
  items: TimeOffOnboardingPreviewItem[]
}

export type TimeOffSummary = {
  employeeId: string;
  country: string,
  timeOffRequests: TimeOffRequest[];
}

export type TimeOffSummariesResponse = {
  eventDaysByCountry: Record<string, EventDay[]>;
  timeOffSummaries: TimeOffSummary[];
}

export const CommonTimeOffName = {
  AnnualLeave: 'Annual Leave',
  SickLeave: 'Sick Leave',
  MaternityLeave: 'Maternity Leave',
  PaternityLeave: 'Paternity Leave',
  UnpaidLeave: 'Unpaid Leave',
  CompassionateLeave: 'Compassionate Leave',
  ExaminationLeave: 'Examination Leave',
  MarriageLeave: 'Marriage Leave',
  CompensationLeave: 'Compensation Leave',
  VolunteerLeave: 'Volunteer Leave',
  ParentalLeave: 'Parental Leave',
  AdoptionLeave: 'Adoption Leave',
  JuryServiceLeave: 'Jury Service Leave',
}
export const TimeOffNameOptions: SelectGroupOption[] = [
  {
    label: 'Common',
    options: [
      { label: 'Annual Leave', value: 'Annual Leave' },
      { label: 'Sick Leave', value: 'Sick Leave' },
      { label: 'Maternity Leave', value: 'Maternity Leave' },
      { label: 'Paternity Leave', value: 'Paternity Leave' },
      { label: 'Unpaid Leave', value: 'Unpaid Leave' },
      { label: 'Compassionate Leave', value: 'Compassionate Leave' },
      { label: 'Examination Leave', value: 'Examination Leave' },
      { label: 'Marriage Leave', value: 'Marriage Leave' },
      { label: 'Compensation Leave', value: 'Compensation Leave' },
      { label: 'Volunteer Leave', value: 'Volunteer Leave' },
      { label: 'Parental Leave', value: 'Parental Leave' },
      { label: 'Adoption Leave', value: 'Adoption Leave' },
      { label: 'Jury Service Leave', value: 'Jury Service Leave' },
    ],
  },

  {
    label: 'JP',
    options: [
      { label: 'Special Paid Leave', value: 'Special Paid Leave' },
      { label: 'Childcare Leave', value: 'Childcare Leave' },
    ],
  },

  {
    label: 'KR',
    options: [
      { label: 'Infertility Treatment Leave', value: 'Infertility Treatment Leave' },
      { label: 'Reserve Military Service', value: 'Reserve Military Service' },
      { label: 'Sickness Leave (Work Injury/Illness Related)', value: 'Sickness Leave (Work Injury/Illness Related)' },
      { label: 'Voting Leave', value: 'Voting Leave' },
      { label: 'Family Care Leave', value: 'Family Care Leave' },
    ],
  },

  {
    label: 'TW',
    options: [
      { label: 'Personal Leave / Family Leave', value: 'Personal Leave / Family Leave' },
      { label: 'Sick Leave (Half Paid)', value: 'Sick Leave (Half Paid)' },
      { label: 'Hospitalization / Tocolysis Leave', value: 'Hospitalization / Tocolysis Leave' },
      { label: 'Menstrual Leave', value: 'Menstrual Leave' },
      { label: 'Pregnancy Check-Up Leave', value: 'Pregnancy Check-Up Leave' },
      { label: 'Miscarriage Leave', value: 'Miscarriage Leave' },
      { label: 'Miscarriage Leave (Half-Paid)', value: 'Miscarriage Leave (Half-Paid)' },
      {
        label: 'Paternity Leave / Accompany Pregnancy Check-Up Leave',
        value: 'Paternity Leave / Accompany Pregnancy Check-Up Leave',
      },
      { label: 'COVID Vaccination Leave', value: 'COVID Vaccination Leave' },
      { label: 'Reserve Military Service', value: 'Reserve Military Service' },
    ],
  },

  {
    label: 'IND',
    options: [
      { label: 'Casual Leave', value: 'Casual Leave' },
    ],
  },

  {
    label: 'AUS',
    options: [
      { label: 'Personal / Carer\'s Leave (Sick Leave)', value: 'Personal / Carer\'s Leave (Sick Leave)' },
      { label: 'Family and Domestic Violence Leave', value: 'Family and Domestic Violence Leave' },
      { label: 'Community Service Leave - Jury Duty', value: 'Community Service Leave - Jury Duty' },
      { label: 'Community Service Leave (Unpaid)', value: 'Community Service Leave (Unpaid)' },
    ],
  },

  {
    label: 'CHN',
    options: [
      { label: 'Sick Leave (Hospitalization)', value: 'Sick Leave (Hospitalization)' },
      { label: 'Extended Sick Leave', value: 'Extended Sick Leave' },
      { label: 'Extended Compassionate Leave (Travel)', value: 'Extended Compassionate Leave (Travel)' },
      { label: 'Child Feeding Leave', value: 'Child Feeding Leave' },
    ],
  },

  {
    label: 'HK',
    options: [
      { label: 'Sabbatical Leave', value: 'Sabbatical Leave' },
      { label: 'COVID Vaccination Leave', value: 'COVID Vaccination Leave' },
      { label: 'Travel Leave', value: 'Travel Leave' },
    ],
  },

  {
    label: 'SG',
    options: [
      { label: 'Hospitalization Leave', value: 'Hospitalization Leave' },
      { label: 'Childcare Leave', value: 'Childcare Leave' },
      { label: 'Extended Childcare Leave', value: 'Extended Childcare Leave' },
      { label: 'Shared Parental Leave', value: 'Shared Parental Leave' },
      { label: 'Infant Care Leave', value: 'Infant Care Leave' },
      { label: 'Reserve Military Service', value: 'Reserve Military Service' },
      { label: 'PH Replacement Leave or Annual Leave (PH)', value: 'PH Replacement Leave or Annual Leave (PH)' },
    ],
  },

  {
    label: 'US',
    options: [
      { label: 'Safe & Sick Days', value: 'Safe & Sick Days' },
      { label: 'New York Paid Family Leave', value: 'New York Paid Family Leave' },
      { label: 'New York Short-Term Disability Leave', value: 'New York Short-Term Disability Leave' },
      { label: 'Military Leave', value: 'Military Leave' },
      { label: 'Voting Leave', value: 'Voting Leave' },
      { label: 'Jury Duty and Witness Leave', value: 'Jury Duty and Witness Leave' },
      { label: 'Witness Leave', value: 'Witness Leave' },
    ],
  },
]


export const TimeOffDurationType = {
  FullDay: 'Full Day',
  FirstHalf: '1st Half Day',
  SecondHalf: '2nd Half Day',
  Hourly: 'Hourly',
}
export const TimeOffDurationTypeOptions = stringArrayToSelectOptions(Object.values(TimeOffDurationType))

export const TimeOffRequestStatus = {
  Pending: 'Pending',
  PendingManagerApproval: 'Pending Manager Approval',
  PendingHcApproval: 'Pending HC Approval',
  Approved: 'Approved',
  Rejected: 'Rejected',
  Cancelled: 'Cancelled',
}
export const TimeOffRequestStatusOptions = stringArrayToSelectOptions(
  Object.values(TimeOffRequestStatus).filter((v) => v !== TimeOffRequestStatus.Pending),
)

export const TimeOffPendingStatuses = [
  TimeOffRequestStatus.PendingManagerApproval,
  TimeOffRequestStatus.PendingHcApproval,
]

// For reports: merges both pending statuses into one "Pending" option
export const TimeOffRequestStatusOptionsForReport = [
  { label: TimeOffRequestStatus.Pending, value: TimeOffRequestStatus.Pending },
  { label: TimeOffRequestStatus.Approved, value: TimeOffRequestStatus.Approved },
  { label: TimeOffRequestStatus.Rejected, value: TimeOffRequestStatus.Rejected },
  { label: TimeOffRequestStatus.Cancelled, value: TimeOffRequestStatus.Cancelled },
]

export const TimeOffReviewTargetGroup = {
  DirectReports: 'Direct Reports',
  ManagedCountries: 'Managed Countries',
}

export const TimeOffReviewTargetGroupOptions: SelectOption[] = [
  {
    label: TimeOffReviewTargetGroup.DirectReports,
    value: TimeOffReviewTargetGroup.DirectReports,
  },
  {
    label: `Countries (HC & Country Head)`,
    value: TimeOffReviewTargetGroup.ManagedCountries,
  },
]

export const TimeOffAccrualType = {
  AccrueAnnually: 'Accrue Annually',
  OnGrant: 'On Grant',
  Unlimited: 'Unlimited',
}
export const TimeOffAccrualTypeOptions = stringArrayToSelectOptions(Object.values(TimeOffAccrualType))
export const TimeOffAccrualTypeDescriptions: Record<string, string> = {
  [TimeOffAccrualType.AccrueAnnually]: 'Time off is accrued at the beginning of each year.',
  [TimeOffAccrualType.OnGrant]: 'Time off is granted by HC manually',
  [TimeOffAccrualType.Unlimited]: 'There is no limit to the amount of time off that can be taken.',
}

export const TimeOffAmountUnit = {
  Day: 'Day',
  Hour: 'Hour',
  Week: 'Week',
}
export const TimeOffAmountUnitOptions = stringArrayToSelectOptions(Object.values(TimeOffAmountUnit))

export const TimeOffCarryForwardType = {
  NotApplicable: 'Not Applicable',
  NoCarryForward: 'No Carry Forward',
  ExpiryInTwoYears: 'Expiry In Two Years',
  CarryForwardWithExpiry: 'Carry Forward with Expiry',
  CarryForwardAllWithExpiry: 'Carry Forward All with Expiry',
  CarryForwardAllWithoutExpiry: 'Carry Forward All without Expiry',
}
export const TimeOffCarryForwardTypeOptions = stringArrayToSelectOptions(Object.values(TimeOffCarryForwardType))
export const TimeOffCarryForwardTypeDescriptions: Record<string, string> = {
  [TimeOffCarryForwardType.NoCarryForward]: 'Unused time off cannot be carried over to the next year.',
  [TimeOffCarryForwardType.ExpiryInTwoYears]: 'It will be expired after 2 years since granted. No cap is applied.',
  [TimeOffCarryForwardType.CarryForwardWithExpiry]: 'Limited unused time off can be carried over to the next year but will expire after a certain date.',
}

export const TimeOffBalanceHistoryType = {
  accrualCredit: 'Accrual Credit',
  carryForward: 'Carry Forward',
  creditAdjusted: 'Credit Adjusted',
  creditExpired: 'Credit Expired',
  timeOffCancelled: 'Time Off Cancelled',
  timeOffRequested: 'Time Off Requested',
  timeOffRejected: 'Time Off Rejected',
}
export const TimeOffBalanceHistoryTypeOptions = stringArrayToSelectOptions(Object.values(TimeOffBalanceHistoryType))
export const TimeOffBalanceHistoryTypeColorMap = {
  [TimeOffBalanceHistoryType.accrualCredit]: 'green',
  [TimeOffBalanceHistoryType.carryForward]: 'blue',
  [TimeOffBalanceHistoryType.creditAdjusted]: 'orange',
  [TimeOffBalanceHistoryType.creditExpired]: 'red',
  [TimeOffBalanceHistoryType.timeOffCancelled]: 'gold',
  [TimeOffBalanceHistoryType.timeOffRequested]: 'purple',
  [TimeOffBalanceHistoryType.timeOffRejected]: 'red',
}

export const TimeOffAllocationStatus = {
  Active: 'Active',
  Inactive: 'Inactive',
  ExpiryProcessed: 'Expiry Processed',
}

export const TimeOffCalendarTargetGroup = {
  DirectReports: 'Direct Reports',
  DirectAndIndirectReports: 'Direct & Indirect Reports',
  Teammates: 'Teammates',
}
export const TimeOffCalendarTargetGroupOptions = stringArrayToSelectOptions(Object.values(TimeOffCalendarTargetGroup))

export const TimeOffAllocationType = {
  CarryForward: 'Carry Forward',
  BaseGrant: 'Base Grant',
  ExtraGrant: 'Extra Grant',
  ProratedBaseGrant: 'Prorated Base Grant',
}